import React from 'react';
import { cn } from '@/helpers/utils';
import { useSelector } from 'react-redux';
import { Carousel, CarouselItem } from '@americanexpress/dls-react';
import { getLocaleData } from '@/selectors/localeSelectors';
import * as loungeHelper from '@/helpers/loungeHelper';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { getLastViewedProduct } from '@/selectors/lastViewedProductSelectors';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import useFeaturedLounge from '@/hooks/loungesHooks/useFeaturedLounge';
import LoungeTypeBadge from '../common/LoungeTypeBadge';
import FeaturedLoungeWaitListInfo from '../waitList/FeaturedLoungeWaitListInfo';
import styles from './FeaturedLounge.module.scss';
import LFLink from '../common/LFLink';

const FeaturedLounge: React.FC = () => {
  const oneCmsBaseImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const placeholderImgUrl = `${oneCmsBaseImgUrl}placeholder.jpg`;
  const lastViewedProduct = useSelector(getLastViewedProduct);
  const { locale } = useSelector(getLocaleData);
  const featuredLounge = useFeaturedLounge();
  const {
    lounges: {
      open: openLabel,
      closed: closedLabel,
      opensAt,
      closesAt,
      featuredLocation,
    },
    config: {
      dateFormatting: { timeFormat },
    },
  } = useCommonContent();

  if (!featuredLounge) return null;
  const {
    isOpen: isLoungeOpen,
    openingTime,
    closingTime,
  } = loungeHelper.getLoungeCurrentStatus(locale, featuredLounge, timeFormat);

  const hasInvalidTimings = !openingTime || !closingTime;

  const loungeTerminal = featuredLounge.location?.terminal;
  const loungePath = loungeHelper.generateLoungeUrl({
    locale,
    loungeId: featuredLounge.id,
    loungeName: featuredLounge.name,
    loungeTerminal,
  });
  const isLoungeTempClosed = loungeHelper.isLoungeTempClosed(featuredLounge);

  const loungeImages = featuredLounge.images?.length
    ? featuredLounge.images
    : [{ images: { medium: placeholderImgUrl }, public_id: 'placeholder' }];

  const loungeOperatingStartStopTime = () => {
    if (isLoungeTempClosed) return null;

    return isLoungeOpen ? (
      <span className="tw-font-normal">
        &nbsp;• {closesAt} {closingTime}
      </span>
    ) : (
      <span className="tw-font-normal">
        &nbsp;• {opensAt} {openingTime}
      </span>
    );
  };

  return (
    <LFLink
      className="tw-w-full"
      aria-label={`${featuredLounge.name} ${loungeTerminal}`}
      to={`${lastViewedProduct}/${featuredLounge?.airport?.iata}/${loungePath}/`}
      key={`featured-lounge-link-${featuredLounge.id}`}
      element="featured-lounge-link"
    >
      <div className={styles.carouselFullHeight}>
        <h2 className="tw-heading-md-regular tw-justify-self-start tw-text-gray-900 sm_tw-col-span-2 lg_tw-col-span-3">
          {featuredLocation}
        </h2>

        <div className="tw-group tw-mt-6 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-gray-300 tw-shadow-low">
          <div className="tw-grid tw-grid-cols-1 md_tw-grid-cols-12">
            <div className="tw-col-span-1 tw-overflow-hidden md_tw-col-span-6 xl_tw-col-span-8">
              <div className="tw-relative tw-h-full">
                <LoungeTypeBadge loungeId={featuredLounge.id} />
                <Carousel
                  id="featured-lounge-carousel"
                  isNavigationOnSlides
                  isSlideFullWidth
                  aria-label="Lounge images"
                  className={cn(
                    'carousel-override featured-lounge-carousel tw-h-full',
                    'no-touch_last-child_first-child_tw-opacity-0 no-touch_last-child_first-child_focus-within_tw-opacity-100 no-touch_last-child_first-child_group-hover_tw-opacity-100 no-touch_last-child_first-child_group-focus_tw-opacity-100',
                    'no-touch_last-child_first-child_tw-duration-200 no-touch_last-child_first-child_group-hover_tw-duration-300'
                  )}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).tagName === 'IMG') return; // don't block image click through

                    // don't navigate to lounge detail page when clicking on the carousel nav
                    e.preventDefault();
                  }}
                >
                  {loungeImages.map(({ images, public_id }, i) => (
                    <CarouselItem
                      key={`lounge-image-${public_id ?? i}`}
                      id={public_id ?? `slide-${i}`}
                      className="tw-h-full tw-w-full tw-flex-shrink-0"
                      style={{ borderRadius: '0' }}
                    >
                      <img
                        src={images?.medium}
                        alt={`Lounge thumbnail ${i + 1}`}
                        width={300}
                        height={200}
                        className="tw-aspect-video tw-min-h-full tw-min-w-full tw-object-cover"
                      />
                    </CarouselItem>
                  ))}
                </Carousel>
              </div>
            </div>
            <div className="tw-col-span-1 tw-flex tw-flex-col tw-justify-between md_tw-col-span-6 xl_tw-col-span-4">
              <div className="tw-mb-6 tw-px-4 tw-pt-6 md_tw-mb-0 md_tw-px-6 md_tw-pt-6 xl_tw-px-8 xl_tw-pt-8">
                <h3 className="tw-heading-md-medium tw-text-gray-900">
                  {featuredLounge.name}
                </h3>
                <div className="tw-mt-1 tw-text-sm tw-text-gray-800 md_tw-text-base">
                  {(!hasInvalidTimings || isLoungeTempClosed) && (
                    <div id={`lounge-status-${featuredLounge.id}`}>
                      {' '}
                      {isLoungeOpen ? (
                        <span className="tw-text-status-success">
                          {openLabel}
                        </span>
                      ) : (
                        <span className="tw-text-status-caution">
                          {closedLabel}
                        </span>
                      )}
                      {loungeOperatingStartStopTime()}
                    </div>
                  )}
                  <p className="tw-truncate">{loungeTerminal}</p>
                </div>
              </div>
              <FeaturedLoungeWaitListInfo loungeId={featuredLounge.id} />
            </div>
          </div>
        </div>
        <hr className="tw-my-10 tw-border-gray-300" />
      </div>
    </LFLink>
  );
};

export default FeaturedLounge;
